// ABC SEO/GEO — sticky top bar. Site/project selector, search, New Audit, bell, avatar.
const { Icon } = window.SGIcons;

// Real, shared domain switcher — driven by the client's TRACKED domains
// (window.SGLive.useLive). Changing it here updates every screen at once.
function SiteSelector({ onNavigate }) {
  const { sites, site, setSite, prettySite } = window.SGLive.useLive();
  const [hover, setHover] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const list = sites || [];
  const loading = sites === null;
  const label = site ? prettySite(site) : (loading ? 'Loading domains…' : 'No domain tracked');
  const initial = (site ? prettySite(site) : 'A').charAt(0).toUpperCase();
  const manage = () => { setOpen(false); if (onNavigate) onNavigate('settings'); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        title="Switch the domain you're analyzing"
        style={{ display: 'flex', alignItems: 'center', gap: 10, height: 42, padding: '0 12px',
          border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer',
          background: hover || open ? 'var(--sg-sunken)' : 'var(--sg-card)', transition: 'background 140ms var(--sg-ease)' }}>
        <span style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--sg-grad-brand)', flex: 'none',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 11 }}>{initial}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: site ? 'var(--sg-text)' : 'var(--sg-text-3)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <Icon name="chevron-down" size={16} color="var(--sg-text-2)" />
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 50, left: 0, minWidth: 280, maxWidth: 360, background: 'var(--sg-card)',
          border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-lg)', boxShadow: '0 14px 40px rgba(15,23,42,0.18)',
          padding: 6, zIndex: 40, animation: 'sg-fade 0.14s var(--sg-ease)' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--sg-text-3)', padding: '8px 10px 6px' }}>Your tracked domains</div>
          {list.length === 0 ? (
            <div style={{ padding: '8px 12px 12px', fontSize: 13, color: 'var(--sg-text-2)', lineHeight: 1.5 }}>
              {loading ? 'Loading…' : 'No domains tracked yet — add one in Settings to start analyzing it.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 320, overflowY: 'auto' }}>
              {list.map((s) => {
                const active = s === site;
                return (
                  <button key={s} onClick={() => { setSite(s); setOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', border: 'none', borderRadius: 'var(--sg-radius-md)',
                      cursor: 'pointer', background: active ? 'var(--sg-blue-50)' : 'transparent', textAlign: 'left', fontFamily: 'var(--sg-font-sans)' }}>
                    <Icon name="globe" size={16} color={active ? 'var(--sg-blue-600)' : 'var(--sg-text-3)'} />
                    <span style={{ flex: 1, fontSize: 13.5, fontWeight: active ? 700 : 500, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prettySite(s)}</span>
                    {active && <Icon name="check" size={15} color="var(--sg-blue-600)" />}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ height: 1, background: 'var(--sg-border)', margin: '6px 4px' }} />
          <button onClick={manage}
            style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 10px', border: 'none', borderRadius: 'var(--sg-radius-md)',
              cursor: 'pointer', background: 'transparent', textAlign: 'left', fontFamily: 'var(--sg-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--sg-blue-600)' }}>
            <Icon name="settings" size={15} /> Add or manage domains…
          </button>
        </div>
      )}
    </div>
  );
}

function SGTopBar({ onToggleSidebar, onNewAudit, onNavigate }) {
  const [btnHover, setBtnHover] = React.useState(false);
  return (
    <header style={{
      height: 64, flex: 'none', background: 'rgba(255,255,255,0.86)', backdropFilter: 'blur(10px)',
      borderBottom: '1px solid var(--sg-border)', display: 'flex', alignItems: 'center',
      gap: 14, padding: '0 22px', position: 'sticky', top: 0, zIndex: 20,
    }}>
      <button onClick={onToggleSidebar} aria-label="Toggle menu" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--sg-text-2)', display: 'inline-flex', padding: 6, borderRadius: 'var(--sg-radius-sm)' }}>
        <Icon name="menu" size={20} />
      </button>

      <SiteSelector onNavigate={onNavigate} />

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, width: 300, height: 42, padding: '0 14px',
          background: 'var(--sg-sunken)', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)' }}>
          <Icon name="search" size={17} color="var(--sg-text-3)" />
          <input placeholder="Search keywords, pages, prompts…" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'var(--sg-font-sans)', fontSize: 13.5, color: 'var(--sg-text)' }} />
        </div>

        <button onClick={onNewAudit} onMouseEnter={() => setBtnHover(true)} onMouseLeave={() => setBtnHover(false)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 42, padding: '0 18px', border: 'none',
            borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 14,
            color: '#fff', background: 'var(--sg-grad-brand)', boxShadow: btnHover ? '0 6px 18px rgba(37,99,235,0.42)' : '0 3px 10px rgba(37,99,235,0.30)',
            transform: btnHover ? 'translateY(-1px)' : 'none', transition: 'all 140ms var(--sg-ease)' }}>
          <Icon name="plus" size={16} /> New Audit
        </button>

        <button aria-label="Notifications" style={{ position: 'relative', width: 42, height: 42, border: '1px solid var(--sg-border)', borderRadius: '50%', background: 'var(--sg-card)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sg-text-2)' }}>
          <Icon name="bell" size={18} />
          <span style={{ position: 'absolute', top: 9, right: 10, width: 8, height: 8, borderRadius: '50%', background: 'var(--sg-red)', border: '2px solid var(--sg-card)' }} />
        </button>

        <span style={{ width: 42, height: 42, borderRadius: '50%', background: 'var(--sg-grad-violet)', flex: 'none',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>AB</span>
      </div>
    </header>
  );
}

window.SGTopBar = SGTopBar;
